import { useState, useCallback } from 'react'
import { IDriver, IGetDriversParams } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'
import * as Api from 'apps/fleet-planner/utils/api'

interface IUseDriverApiState {
  error?: string
  isLoading: boolean
  drivers: IDriver[]
  isDriverListLoading: boolean
  resetError: () => void
  getDrivers: (params: IGetDriversParams) => Promise<Types.IDriver[] | undefined>
  /**
   * PUT request to update the driver information
   */
  updateDriver: (
    driverId: string,
    changes: Partial<Types.IDriver>
  ) => Promise<Types.IDriver | undefined>
  /**
   * POST request to create new driver
   */
  createNewDriver: (driver: Partial<Types.IDriver>) => Promise<Types.IDriver | undefined>
  lastUpdatedDriverId?: string
}

/**
 * Hook used to create/update driver entities
 */
export const useDriverApi = (): IUseDriverApiState => {
  const [isDriverListLoading, setIsDriverListLoading] = useState<boolean>(false)
  const [drivers, setDrivers] = useState<IDriver[]>([])
  const [isLoading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const resetError = useCallback(() => setError(''), [])

  /**
   * Update driver with new information
   */
  // TODO: replace with useFetchData here
  const updateDriver = useCallback(async (driverId: string, changes: Partial<Types.IDriver>) => {
    setLoading(true)
    try {
      const { driver } = await Api.updateDriver(driverId, changes)
      setError('')
      setLoading(false)
      return driver
    } catch (e) {
      setError(e)
      setLoading(false)
      return undefined
    }
  }, [])

  /**
   * Create new driver entity
   */
  const createNewDriver = useCallback(async (d: Partial<Types.IDriver>): Promise<
    Types.IDriver | undefined
  > => {
    setLoading(true)
    try {
      const { driver } = await Api.createDriver(d)
      setError('')
      setLoading(false)
      return driver
    } catch (e) {
      setError(e)
      setLoading(false)
      return undefined
    }
  }, [])

  /**
   * Fetch drivers data + return the drivers list
   */
  const getDrivers = useCallback(async (params: IGetDriversParams): Promise<
    Types.IDriver[] | undefined
  > => {
    setError('')
    setIsDriverListLoading(true)
    try {
      const { drivers: response } = await Api.fetchAllDrivers(params)
      setDrivers(response)
      setIsDriverListLoading(false)
      return response
    } catch (e) {
      setError(e)
      setIsDriverListLoading(false)
      return undefined
    }
  }, [])

  return {
    updateDriver,
    createNewDriver,
    error,
    resetError,
    isLoading,
    getDrivers,
    drivers,
    isDriverListLoading,
  }
}
